import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Plus } from 'lucide-react'

import type { PackagingCountLogEntry } from '@/lib/api/packaging-count-log-api'
import { Button } from '@/components/ui/button'
import {
    HEADER_CELL,
    LEDGER_COLS,
    LEDGER_MIN_W,
} from '@/components/count-log/ledger-grid'
import { LogEntryEditRow } from '@/components/count-log/log-entry-edit-row'
import { LogEntryRow } from '@/components/count-log/log-entry-row'
import { issuedTotal } from '@/components/count-log/gap'
import { useAuth } from '@/hooks/use-auth'
import { usePackagingCountLogEntriesInfinite } from '@/hooks/use-packaging-count-log-query'
import { LedgerDayPager } from '@/components/common/ledger-day-pager'
import { useFetchAllPages } from '@/hooks/use-fetch-all-pages'
import { useLedgerNewRows } from '@/hooks/use-ledger-new-rows'
import { todayYMD } from '@/lib/date-presets'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/packaging-warehouse/count-log/',
)({
    component: PackagingCountLogPage,
})

function PackagingCountLogPage() {
    const { hasPermission } = useAuth()
    // Sổ có quyền riêng packaging_count_logs.* (M-61) — tách khỏi
    // packaging_lots.* để tablet của đội sản xuất ghi sổ mà không mở được các
    // màn kho vật tư. Ghi sổ và DUYỆT phiếu vẫn là hai quyền khác nhau: người
    // ghi sổ không đương nhiên được trừ kho.
    const canApproveNotes =
        hasPermission?.('packaging_lots.approve_out') ?? true
    const canWriteLog = hasPermission?.('packaging_count_logs.write') ?? true
    // Mở màn phiếu cần quyền xem kho vật tư; không có thì mã phiếu chỉ là chữ.
    const canOpenNotes = hasPermission?.('packaging_lots.read') ?? true

    // Sổ lật theo NGÀY như sổ giấy ngoài xưởng: mỗi trang là trọn một ngày.
    const [day, setDay] = useState(todayYMD)
    const query = usePackagingCountLogEntriesInfinite({
        date_from: day,
        date_to: day,
    })
    const { data, isLoading } = query
    // Ngày cao điểm có thể vượt một trang API — kéo nốt cho đủ sổ, vì "còn
    // trang nữa" không phải thứ người ghi sổ phải bấm.
    useFetchAllPages(query)
    // Các trang đã tải nối lại thành một danh sách; server đã xếp ngày mới nhất
    // lên trước nên không sắp xếp lại ở đây.
    const entries = useMemo(
        () => (data?.pages ?? []).flatMap((p) => p.data ?? []),
        [data],
    )

    // Nhập ngay trong bảng: `newRows` là các dòng mẻ MỚI đang mở ở đầu bảng
    // (mở được nhiều dòng cùng lúc, M-196), `editingId` là mẻ cũ đang sửa tại
    // chỗ — sửa mẻ cũ không đóng các dòng mới (đóng là mất chữ đang gõ).
    const newRows = useLedgerNewRows({
        ledger: 'packaging_count',
        canWrite: canWriteLog,
    })
    const [editingId, setEditingId] = useState<string | undefined>()

    const startEdit = (entry: PackagingCountLogEntry) => setEditingId(entry.id)
    const closeEdit = () => setEditingId(undefined)
    // Sau khi LƯU thì còn phải nhảy sang đúng ngày của mẻ vừa lưu: dòng nhập giữ
    // nguyên ngày lúc mở, nên người ghi có thể lật sang trang khác rồi mới bấm
    // Lưu. Không nhảy thì bảng không đổi gì và họ tưởng mất công, ghi lại lần
    // nữa thành mẻ trùng.
    //
    // Hàm RIÊNG chứ không nối thẳng vào onClick: React truyền MouseEvent vào
    // tham số đầu — nhận nhầm nó làm ngày là vỡ cả trang.
    const jumpTo = (savedDay?: string) => {
        if (typeof savedDay === 'string' && savedDay) setDay(savedDay)
    }

    const open = entries.filter((e) => e.status === 'counting')
    const waiting = entries.filter((e) => e.status === 'pending')
    // Tách hai cột số theo ĐÃ TRỪ KHO hay CHƯA: túi trên phiếu chờ duyệt vẫn
    // còn nguyên trong kho, gộp chung với phần đã duyệt là đọc ra số ảo.
    const waitingQty = waiting.reduce((s, e) => s + issuedTotal(e.lines), 0)
    const approvedQty = entries
        .filter((e) => e.status === 'approved')
        .reduce((s, e) => s + issuedTotal(e.lines), 0)

    return (
        <div className="ledger-text space-y-4 page-pad">
            <header className="border-b-2 border-foreground pb-3">
                <h1 className="text-2xl font-bold md:text-3xl">
                    {m.count_log_title()}
                </h1>
                <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                    {m.count_log_subtitle()}
                </p>
            </header>

            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 border-b pb-3">
                <span className="flex items-baseline gap-2">
                    <b className="text-xl tabular-nums">{open.length}</b>
                    <span className="text-sm text-muted-foreground">
                        {m.count_log_stat_counting()}
                    </span>
                </span>
                <span className="flex items-baseline gap-2">
                    <b className="text-xl tabular-nums">{waiting.length}</b>
                    <span className="text-sm text-muted-foreground">
                        {m.count_log_stat_ready()}
                    </span>
                </span>
                <span className="flex items-baseline gap-2">
                    <b className="text-xl tabular-nums text-amber-700">
                        {formatQty(waitingQty)}
                    </b>
                    <span className="text-sm text-muted-foreground">
                        {m.count_log_stat_short()}
                    </span>
                </span>
                <span className="flex items-baseline gap-2">
                    <b className="text-xl tabular-nums text-primary">
                        {formatQty(approvedQty)}
                    </b>
                    <span className="text-sm text-muted-foreground">
                        {m.count_log_stat_over()}
                    </span>
                </span>
                {/* Bốn số trên chỉ cộng các mẻ ĐÃ TẢI, không phải toàn bộ sổ. */}
                <span className="text-xs text-muted-foreground">
                    {m.count_log_stat_scope({ n: entries.length })}
                </span>
            </div>

            {/* Nút thêm nằm TRÊN bảng: mẻ mới chèn vào đầu danh sách. */}
            <LedgerDayPager value={day} onChange={setDay} />

            {/* Nút thêm hiện ở MỌI ngày: đang mở trang nào cũng phải ghi được
                mẻ mới ngay, không phải lật về hôm nay trước. */}
            {canWriteLog && (
                <Button
                    type="button"
                    variant="outline"
                    className="h-12"
                    onClick={newRows.add}
                >
                    <Plus className="mr-1 size-4" />
                    {m.count_log_btn_add_row()}
                </Button>
            )}

            {/* Bảng sổ — kẻ ô như sổ giấy của thủ kho. */}
            <section className="overflow-x-auto rounded-md border bg-card">
                <div className={LEDGER_MIN_W}>
                    <div
                        className={`grid gap-0 border-b bg-muted/40 text-xs font-semibold uppercase tracking-wide text-muted-foreground ${LEDGER_COLS}`}
                    >
                        <div className={HEADER_CELL}>
                            {m.count_log_col_date()}
                        </div>
                        <div className={HEADER_CELL}>
                            {m.count_log_col_material()}
                        </div>
                        <div className={`${HEADER_CELL} text-right`}>
                            {m.count_log_col_opening()}
                        </div>
                        <div className={`${HEADER_CELL} text-right`}>
                            {m.count_log_col_issued()}
                        </div>
                        <div className={`${HEADER_CELL} text-right`}>
                            {m.count_log_col_closing()}
                        </div>
                        <div className={`${HEADER_CELL} text-right`}>
                            {m.count_log_col_gap()}
                        </div>
                        <div className={HEADER_CELL}>
                            {m.count_log_col_status()}
                        </div>
                    </div>

                    {isLoading && (
                        <p className="p-6 text-center text-muted-foreground">
                            {m.count_log_loading()}
                        </p>
                    )}
                    {!isLoading &&
                        entries.length === 0 &&
                        newRows.rows.length === 0 && (
                            <p className="p-6 text-center text-muted-foreground">
                                {m.count_log_empty()}
                            </p>
                        )}

                    {newRows.rows.map((row) => (
                        <LogEntryEditRow
                            key={row.id}
                            defaultDate={day}
                            onDone={(savedDay) => {
                                jumpTo(savedDay)
                                newRows.remove(row.id)
                            }}
                            onCancel={() => newRows.remove(row.id)}
                        />
                    ))}

                    {entries.map((entry) =>
                        editingId === entry.id ? (
                            <LogEntryEditRow
                                key={entry.id}
                                entry={entry}
                                onDone={(savedDay) => {
                                    jumpTo(savedDay)
                                    closeEdit()
                                }}
                                onCancel={closeEdit}
                            />
                        ) : (
                            <LogEntryRow
                                key={entry.id}
                                entry={entry}
                                canApproveNotes={canApproveNotes}
                                canWriteLog={canWriteLog}
                                canOpenNotes={canOpenNotes}
                                onEdit={startEdit}
                            />
                        ),
                    )}
                </div>
            </section>
        </div>
    )
}
